/**
 * Deterministic math for the simulation (ARCHITECTURE §4).
 *
 * `Math.sin/cos/atan2/exp/pow/log` are NOT guaranteed bit-identical across JS engines/CPUs, so the sim must use
 * these instead. They are built ONLY from IEEE-754 `+ - * /`, `Math.sqrt`, `Math.round` and `Math.abs`, which are
 * exactly specified, so results are bit-identical on every browser and on Node.
 *
 * Accuracy: |dsin - Math.sin|, |dcos - Math.cos| < 1e-12 and |datan2 - Math.atan2| < 1e-7 (tests assert < 1e-4).
 * Polynomials are evaluated in a fixed order (Horner). Do not "optimize" the expressions: reordering changes bits.
 */

export const PI = 3.141592653589793;
export const TAU = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
export const DEG_TO_RAD = PI / 180;
export const RAD_TO_DEG = 180 / PI;

// Taylor coefficients for sin (degree 17) and cos (degree 18) on [-π/2, π/2] ⇒ error < 1e-12.
const S3 = -1 / 6;
const S5 = 1 / 120;
const S7 = -1 / 5040;
const S9 = 1 / 362880;
const S11 = -1 / 39916800;
const S13 = 1 / 6227020800;
const S15 = -1 / 1307674368000;
const S17 = 1 / 355687428096000;
const C2 = -1 / 2;
const C4 = 1 / 24;
const C6 = -1 / 720;
const C8 = 1 / 40320;
const C10 = -1 / 3628800;
const C12 = 1 / 479001600;
const C14 = -1 / 87178291200;
const C16 = 1 / 20922789888000;
const C18 = -1 / 6402373705728000;

const sinPoly = (r: number): number => {
  const r2 = r * r;
  return (
    r * (1 + r2 * (S3 + r2 * (S5 + r2 * (S7 + r2 * (S9 + r2 * (S11 + r2 * (S13 + r2 * (S15 + r2 * S17))))))))
  );
};
const cosPoly = (r: number): number => {
  const r2 = r * r;
  return (
    1 +
    r2 *
      (C2 + r2 * (C4 + r2 * (C6 + r2 * (C8 + r2 * (C10 + r2 * (C12 + r2 * (C14 + r2 * (C16 + r2 * C18))))))))
  );
};

/** Wrap an angle to [-π, π]. */
export const wrapAngle = (a: number): number => a - TAU * Math.round(a / TAU);

/** Deterministic sine. */
export const dsin = (x: number): number => {
  let r = wrapAngle(x);
  // Fold into [-π/2, π/2] using sin(π - r) = sin(r).
  if (r > HALF_PI) r = PI - r;
  else if (r < -HALF_PI) r = -PI - r;
  return sinPoly(r);
};

/** Deterministic cosine (dcos(0) === 1 exactly). */
export const dcos = (x: number): number => {
  const r = Math.abs(wrapAngle(x));
  // cos(r) = -cos(π - r) for r in (π/2, π].
  return r > HALF_PI ? -cosPoly(PI - r) : cosPoly(r);
};

/**
 * `out.c = dcos(x)`, `out.s = dsin(x)` — bit-identical to dcos / dsin, for per-tick hot paths (see `datan2Into`:
 * no double is returned from a call, so nothing is boxed). Same operations in the same order.
 */
export const dsinCosInto = (out: { c: number; s: number }, x: number): void => {
  const w = x - TAU * Math.round(x / TAU);
  // sine: fold into [-π/2, π/2] using sin(π - r) = sin(r)
  const rs = w > HALF_PI ? PI - w : w < -HALF_PI ? -PI - w : w;
  const s2 = rs * rs;
  out.s =
    rs *
    (1 + s2 * (S3 + s2 * (S5 + s2 * (S7 + s2 * (S9 + s2 * (S11 + s2 * (S13 + s2 * (S15 + s2 * S17))))))));
  // cosine: cos(r) = -cos(π - r) for r in (π/2, π]
  const ra = Math.abs(w);
  const flip = ra > HALF_PI;
  const rc = flip ? PI - ra : ra;
  const c2 = rc * rc;
  const c =
    1 +
    c2 *
      (C2 + c2 * (C4 + c2 * (C6 + c2 * (C8 + c2 * (C10 + c2 * (C12 + c2 * (C14 + c2 * (C16 + c2 * C18))))))));
  out.c = flip ? -c : c;
};

// atan Taylor coefficients, used on |a| <= tan(π/8) ≈ 0.4142 after argument halving.
const A3 = -1 / 3;
const A5 = 1 / 5;
const A7 = -1 / 7;
const A9 = 1 / 9;
const A11 = -1 / 11;
const A13 = 1 / 13;
const A15 = -1 / 15;
const A17 = 1 / 17;

const atanSmall = (a: number): number => {
  const a2 = a * a;
  return (
    a * (1 + a2 * (A3 + a2 * (A5 + a2 * (A7 + a2 * (A9 + a2 * (A11 + a2 * (A13 + a2 * (A15 + a2 * A17))))))))
  );
};

/** Deterministic arctangent. */
export const datan = (x: number): number => {
  if (x !== x) return x; // NaN
  const neg = x < 0;
  let v = neg ? -x : x;
  let invert = false;
  if (v > 1) {
    v = 1 / v;
    invert = true;
  }
  // atan(v) = 2·atan(v / (1 + sqrt(1 + v²))), maps [0, 1] → [0, tan(π/8)].
  const h = v / (1 + Math.sqrt(1 + v * v));
  let r = 2 * atanSmall(h);
  if (invert) r = HALF_PI - r;
  return neg ? -r : r;
};

/** Deterministic atan2(y, x) in [-π, π]. atan2(0, 0) = 0. */
export const datan2 = (y: number, x: number): number => {
  if (x > 0) return datan(y / x);
  if (x < 0) return y >= 0 ? datan(y / x) + PI : datan(y / x) - PI;
  if (y > 0) return HALF_PI;
  if (y < 0) return -HALF_PI;
  return 0;
};

/**
 * `datan2(y, x)` written into `out.v`, bit-identical to `datan2` — for per-tick hot paths: a double returned from
 * a call the optimizer does not inline is boxed into a fresh HeapNumber, so the sim's flight code reads the angle
 * from a scratch object instead (zero steady-state allocation). Same operations in the same order as
 * datan2 → datan → atanSmall.
 */
export const datan2Into = (out: { v: number }, y: number, x: number): void => {
  if (!(x > 0) && !(x < 0)) {
    // x is 0 (or NaN, which datan2 treats the same way)
    out.v = y > 0 ? HALF_PI : y < 0 ? -HALF_PI : 0;
    return;
  }
  const q = y / x;
  if (q !== q) {
    out.v = q;
    return;
  }
  const neg = q < 0;
  const v0 = neg ? -q : q;
  const invert = v0 > 1;
  const v = invert ? 1 / v0 : v0;
  const h = v / (1 + Math.sqrt(1 + v * v));
  const a2 = h * h;
  const r0 =
    2 *
    (h *
      (1 + a2 * (A3 + a2 * (A5 + a2 * (A7 + a2 * (A9 + a2 * (A11 + a2 * (A13 + a2 * (A15 + a2 * A17)))))))));
  const r1 = invert ? HALF_PI - r0 : r0;
  const t = neg ? -r1 : r1;
  out.v = x > 0 ? t : y >= 0 ? t + PI : t - PI;
};

/** IEEE sqrt is correctly rounded, hence deterministic. Re-exported for clarity at call sites. */
export const dsqrt = Math.sqrt;

export const dhypot = (x: number, y: number): number => Math.sqrt(x * x + y * y);

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Move `cur` toward `target` by at most `maxDelta`. */
export const approach = (cur: number, target: number, maxDelta: number): number =>
  cur < target
    ? cur + maxDelta > target
      ? target
      : cur + maxDelta
    : cur - maxDelta < target
      ? target
      : cur - maxDelta;
/** Sign without -0 / NaN surprises: returns -1, 0 or 1. */
export const sgn = (v: number): -1 | 0 | 1 => (v > 0 ? 1 : v < 0 ? -1 : 0);
/** Squared distance (no sqrt). */
export const dist2 = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};
/**
 * Is point (px, py) inside the axis-aligned ellipse centred at (cx, cy) with radii (rx, ry), inflated by `pad`?
 * Used for hitbox tests.
 */
export const inEllipse = (
  px: number,
  py: number,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  pad = 0,
): boolean => {
  const dx = (px - cx) / (rx + pad);
  const dy = (py - cy) / (ry + pad);
  return dx * dx + dy * dy <= 1;
};
